import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import PageLayout from '../components/PageLayout';

const RescheduleBooking = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Time slots
  const timeSlots = [
    { id: '09:00', label: '9:00 AM', popular: true },
    { id: '10:00', label: '10:00 AM', popular: true },
    { id: '11:00', label: '11:00 AM', popular: false },
    { id: '12:00', label: '12:00 PM', popular: false },
    { id: '13:00', label: '1:00 PM', popular: true },
    { id: '14:00', label: '2:00 PM', popular: true },
    { id: '15:00', label: '3:00 PM', popular: false },
    { id: '16:00', label: '4:00 PM', popular: false },
    { id: '17:00', label: '5:00 PM', popular: false }
  ];

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/${bookingId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch booking details');
        }

        const data = await response.json();
        setBooking(data);
        
        // Set initial date and time from current booking
        if (data.serviceDate) {
          setSelectedDate(data.serviceDate.split('T')[0]);
        }
        if (data.serviceTime) {
          const time = new Date(data.serviceTime).toTimeString().slice(0, 5);
          setSelectedTime(time);
        }
      } catch (err) {
        setError('Failed to load booking details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId]);

  // Generate available dates (next 14 days, excluding Sundays)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Skip Sundays (day 0)
      if (date.getDay() !== 0) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: format(date, 'EEE, MMM d'),
          isWeekend: date.getDay() === 6
        });
      }
    }
    
    return dates;
  };

  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Please select both date and time');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          serviceDate: selectedDate,
          serviceTime: selectedTime
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reschedule booking');
      }

      // Navigate back to booking details
      navigate(`/bookings/${bookingId}`);
    } catch (err) {
      setError(err.message || 'Failed to reschedule booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress size={60} />
          </Box>
        </Container>
      </PageLayout>
    );
  }

  if (!booking) {
    return (
      <PageLayout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">Booking not found</Alert>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/bookings/${bookingId}`)}
        >
          Back to Booking
        </Button>
        <Typography variant="h4" component="h1">
          Reschedule Booking
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Date Selection */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon /> Select New Date
            </Typography>
            
            <Grid container spacing={1}>
              {generateAvailableDates().map((date) => (
                <Grid item xs={6} sm={4} key={date.value}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedDate === date.value ? 2 : 1,
                      borderColor: selectedDate === date.value ? 'primary.main' : 'grey.300',
                      opacity: date.isWeekend ? 0.8 : 1,
                      '&:hover': {
                        borderColor: 'primary.main'
                      }
                    }}
                    onClick={() => setSelectedDate(date.value)}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="body2">
                        {date.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Time Selection */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon /> Select New Time
            </Typography>
            
            <Grid container spacing={2}>
              {timeSlots.map((slot) => (
                <Grid item xs={6} sm={4} key={slot.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedTime === slot.id ? 2 : 1,
                      borderColor: selectedTime === slot.id ? 'primary.main' : 'grey.300',
                      '&:hover': {
                        borderColor: 'primary.main'
                      }
                    }}
                    onClick={() => setSelectedTime(slot.id)}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="subtitle2">
                        {slot.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => navigate(`/bookings/${bookingId}`)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleReschedule}
              disabled={!selectedDate || !selectedTime || submitting}
            >
              {submitting ? 'Rescheduling...' : 'Confirm New Schedule'}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Container>
    </PageLayout>
  );
};

export default RescheduleBooking;